/**
 * Poster frames, extracted in the browser. Task 10.2, requirement 8.11.
 *
 * A `<video>` element decodes the file, a `<canvas>` copies one frame out of it, and the canvas hands
 * back a JPEG. That is the whole mechanism, and the reason it matters is what it *avoids*: requirement
 * 8.11 calls this out as explicitly not transcoding, which is what lets v1 ship with **no server-side
 * video processing whatsoever**. No MediaConvert, no ffmpeg layer, no queue, no IAM for any of it.
 *
 * The trade is honest and worth stating. This only works where the browser can decode the file, and
 * requirement 8.10 already accepts that a `.mov` from an iPhone will not decode in Chrome. So a video
 * uploaded from Safari gets a poster and the same video uploaded from Chrome may not. That degrades to
 * "no poster", which the UI already has to handle for a video still uploading — not to a broken record.
 */

/** Where in the clip to grab. */
const SEEK_FRACTION = 0.25;
/** Longest edge of the poster. A heat card shows it small; the champion screen is the largest use. */
const MAX_EDGE = 1280;
const QUALITY = 0.82;
/** A silent failure is fine, a hang is not: a file the browser cannot decode may never fire an event. */
const TIMEOUT_MS = 12_000;

export interface VideoDetails {
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  /** Null when the browser could not decode the file. Not an error — see the module note. */
  poster: Blob | null;
}

/**
 * Reads a clip's dimensions and duration, and grabs a poster frame.
 *
 * Seeks a quarter of the way in rather than to zero. The first frame of a phone video is very often
 * the moment before the camera settled — a blur, a thumb, a floor — and a race lasts three to ten
 * seconds (requirement 8.12), so a quarter in is reliably the truck actually moving.
 */
export async function inspectVideo(file: Blob): Promise<VideoDetails> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "metadata";
  // Muted and inline, or iOS refuses to load frames without a user gesture on the element itself.
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  try {
    const metadata = await settle(video, "loadedmetadata");
    if (!metadata) return { durationSeconds: null, width: null, height: null, poster: null };

    const duration = Number.isFinite(video.duration) ? video.duration : null;
    const width = video.videoWidth || null;
    const height = video.videoHeight || null;

    // Seeking has to complete before the canvas has anything to copy. Clamped away from the very end
    // because seeking to exactly `duration` lands past the last frame in some browsers and never fires.
    if (duration) {
      video.currentTime = Math.min(duration * SEEK_FRACTION, Math.max(0, duration - 0.05));
      const seeked = await settle(video, "seeked");
      if (!seeked) return { durationSeconds: duration, width, height, poster: null };
    }

    return { durationSeconds: duration, width, height, poster: draw(video, width, height) };
  } finally {
    // Always, even on the failure paths: an un-revoked object URL keeps the whole file in memory, and
    // these are tens of megabytes each.
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

/** Resolves true when the event fires, false on error or timeout. Never rejects, never hangs. */
function settle(video: HTMLVideoElement, event: "loadedmetadata" | "seeked"): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      video.removeEventListener(event, onEvent);
      video.removeEventListener("error", onError);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const onEvent = () => finish(true);
    const onError = () => finish(false);

    video.addEventListener(event, onEvent, { once: true });
    video.addEventListener("error", onError, { once: true });
    const timer = window.setTimeout(() => finish(false), TIMEOUT_MS);
  });
}

function draw(video: HTMLVideoElement, width: number | null, height: number | null): Blob | null {
  if (!width || !height) return null;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext("2d");
  if (!context) return null;
  try {
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
  } catch {
    // A frame the browser decoded but will not let us read. Nothing to be done, and nothing broken.
    return null;
  }
  return dataUrlToBlob(canvas.toDataURL("image/jpeg", QUALITY));
}

/**
 * `toDataURL` then decode, rather than `toBlob`.
 *
 * `toBlob` is callback-based and, on older WebKit, silently never calls back for a canvas this size.
 * The base64 round trip costs a few milliseconds on a 60 KB JPEG and always returns.
 */
function dataUrlToBlob(dataUrl: string): Blob | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: "image/jpeg" });
}

/**
 * Whether this browser is likely to play this type. Requirement 8.10.
 *
 * `canPlayType` rather than sniffing the user agent, because the honest answer is browser- *and*
 * hardware-dependent: Chrome's HEVC support depends on the machine it is running on, so the same build
 * gives different answers on different laptops. Asking the element is the only check that tracks that.
 *
 * Returns false for `""` and true for `"maybe"`. "Maybe" is as good as it gets for HEVC in a `.mov`
 * container even in Safari, so treating it as no would refuse playback on the one browser where it
 * works.
 */
export function canProbablyPlay(contentType: string | null | undefined): boolean {
  if (!contentType) return false;
  const video = document.createElement("video");
  return video.canPlayType(contentType) !== "";
}
